import { Box, Flex, Grid, GridItem, Image, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";

import useFormatter from "@/hooks/useFormatter";
import bonuses from "@/sections/referidos/bonuses.json";

const DEFAULT_CITY = "Guadalajara";

// Maps the display city name to the folder name in the boomerang S3 bucket
// (served via media.grupoguia.mx). Folder names are plain ASCII.
const CITY_IMAGE_DIRS = {
    Guadalajara: "Guadalajara",
    Cancún: "Cancun",
    Querétaro: "Queretaro",
    Tapalpa: "Tapalpa",
    León: "Leon",
};

const MEDIA_BASE = "https://media.grupoguia.mx/assets/guiaconnect/develops";

// `imgExt`/`logoExt` in bonuses.json: omitted → use default; explicit
// null → no file in bucket (don't render that asset).
const resolveExt = (value, fallback) => (value === undefined ? fallback : value);

const buildUrl = (kind, city, slug, ext) =>
    slug && ext && CITY_IMAGE_DIRS[city]
        ? `${MEDIA_BASE}/${kind}/${CITY_IMAGE_DIRS[city]}/${slug}.${ext}`
        : null;

const Develops = () => {
    const { NumberF } = useFormatter();
    const cities = Object.keys(bonuses);
    const [city, setCity] = useState(cities.includes(DEFAULT_CITY) ? DEFAULT_CITY : cities[0]);

    if (!cities.length) return null;

    const develops = bonuses[city] ?? [];

    return (
        <Box mx="auto" maxW="7xl" px={{ base: 10, md: 0 }}>
            <Stack>
                <Flex justifyContent="center" direction={{ base: "column", md: "row" }}>
                    {cities.map((c) => (
                        <Box
                            key={c}
                            px={4}
                            borderBottomWidth={city === c ? "4px" : "1px"}
                            borderColor="black"
                            onClick={(e) => {
                                e.preventDefault();
                                setCity(c);
                            }}
                            cursor="pointer"
                        >
                            <Text colorPalette="teal" variant="outline">
                                {c}
                            </Text>
                        </Box>
                    ))}
                </Flex>
            </Stack>
            <Stack mt={12}>
                <Grid
                    templateColumns={{
                        base: "repeat(1, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: develops.length > 3 ? "repeat(4, 1fr)" : "repeat(3, 1fr)",
                    }}
                    gap={8}
                >
                    {develops.map((develop) => {
                        const imgExt = resolveExt(develop.imgExt, "png");
                        const logoExt = resolveExt(develop.logoExt, "svg");
                        const img = buildUrl("image", city, develop.slug, imgExt);
                        const logo = buildUrl("logo", city, develop.slug, logoExt);
                        return (
                            <GridItem key={develop.name} colSpan={1}>
                                <Box
                                    borderRadius="lg"
                                    boxShadow="lg"
                                    maxW="350px"
                                    h="120px"
                                    overflow="hidden"
                                    _hover={{ boxShadow: "xl" }}
                                    cursor="pointer"
                                >
                                    {img ? (
                                        <Flex h="100%">
                                            <Box w="30%" h="100%">
                                                <Image
                                                    src={img}
                                                    alt={develop.name}
                                                    objectFit="cover"
                                                    w="100%"
                                                    h="100%"
                                                />
                                            </Box>
                                            <Stack
                                                p={4}
                                                w="70%"
                                                alignItems="center"
                                                justifyContent="center"
                                                gap={1}
                                            >
                                                {logo && (
                                                    <Image
                                                        src={logo}
                                                        maxH="40px"
                                                        maxW="100%"
                                                        objectFit="contain"
                                                        alt={develop.name}
                                                    />
                                                )}
                                                <Text
                                                    fontWeight="500"
                                                    fontSize="xs"
                                                    textAlign="center"
                                                    color="gray.700"
                                                >
                                                    {develop.name}
                                                </Text>
                                                <Text fontWeight="bold" fontSize="sm">
                                                    Bono ${NumberF(develop.bonus, false, "", 2)}
                                                </Text>
                                            </Stack>
                                        </Flex>
                                    ) : (
                                        <Stack
                                            p={4}
                                            h="100%"
                                            alignItems="center"
                                            gap={2}
                                            textAlign="center"
                                            justifyContent="center"
                                        >
                                            <Text fontWeight="600" fontSize="sm">
                                                {develop.name}
                                            </Text>
                                            <Text fontWeight="bold" fontSize="sm">
                                                Bono ${NumberF(develop.bonus, false, "", 2)}
                                            </Text>
                                        </Stack>
                                    )}
                                </Box>
                            </GridItem>
                        );
                    })}
                </Grid>
            </Stack>
        </Box>
    );
};
export default Develops;
